import { ClickEvent, Image, Label, Layout, Panel, SelectionType, Table, TableRow, TableRowDisplayEvent, TableSelectionEvent } from "@mcleod/components";
import { AutogenLayoutMultiPreassignment } from "./autogen/AutogenLayoutMultiPreassignment";
import { PlanningUtil } from "../planning/PlanningUtil";
import { EquipmentGroupValidation, EquipmentGroupValidationResult } from "./EquipmentGroupValidation";
import { EquipmentGroupWarnings } from "./EquipmentGroupWarnings";
import { CommonTooltips } from "@mcleod/common";

export class MultiPreassignment extends AutogenLayoutMultiPreassignment {
    public tractorId: string;
    public driverId: string;
    public driver2Id: string;
    public linkedId: string;
    private lastValidCheckState: Map<number, boolean>;
    private _validationResults: Map<string, EquipmentGroupValidationResult>;

    get table(): Table {
        return this.tableMultiPreassignments;
    }

    get validationResults() {
        const serverVR = this.mainDataSource.summaryData?.get("validation_result", null);
        if (serverVR != null && this._validationResults == null) {
            this.setLastValidCheckState();
            this._validationResults = EquipmentGroupValidation.createBatchResult(serverVR);
            if (!EquipmentGroupValidation.batchHasErrors(this._validationResults)) {
                this.applyLastValidCheckState();
            }
        }
        return this._validationResults;
    }

    set validationResults(value: Map<string, EquipmentGroupValidationResult>) {
        this._validationResults = value;
    }

    tableMultiPreassignmentsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data;
        const orderId = tableRow.findComponentById("labelOrderNumber") as Label;
        if (!modelRow.isNull("manifest_id")) {
            orderId.visible = false;
            const manifestIdLabel = tableRow.findComponentById("labelManifestId") as Label;
            manifestIdLabel.visible = true;
            manifestIdLabel.addUnderlineWhenMouseOver();
        }
        else if (!modelRow.isNull("order_id"))
            orderId.addUnderlineWhenMouseOver();
        if (!modelRow.isNull("linked_id")) {
            const imageLinked = tableRow.findComponentById("imageLinked") as Image;
            imageLinked.visible = true;
            CommonTooltips.setTooltipContentLayout(imageLinked, null, "lme/dispatch/planning/LinkedMovesQuickInfoTable"
                , null, { linked_id: modelRow.get("linked_id"), selected_movement_id: modelRow.get("movement_id") }
            );
        }
        const imageHold = tableRow.findComponentById("imageHold") as Image;
        imageHold.visible = modelRow.getBoolean("hold", false);
        if (!modelRow.isNull("hold_reason")) {
            imageHold.tooltip = modelRow.get("hold_reason");
        }
        this.updateValidationState(tableRow);
    }

    tableMultiPreassignmentsOnSelect(event: TableSelectionEvent) {
        if (this.linkedId != null) {
            this.autoSelectLinkedRows(event.oldIndexes.length == this.table.rowCount);
        }
        this.updateTitleNumbers();
    }

    autoSelectLinkedRows(clearSelection: boolean) {
        if (clearSelection) {
            this.table.selectedRows = [];
        } else {
            this.table.rows.forEach(row => {
                if (!row.selected) {
                    this.table.selectRow(row, SelectionType.DISTINCT, null);
                }
            });
        }
    }

    hasError(): boolean {
        return this.tableMultiPreassignments.rows.some(row => {
            const imageError = row.findComponentById("imageError") as Image;
            const imageServerError = row.findComponentById("imageServerError") as Image;
            return imageError.visible || imageServerError.visible;
        });
    }

    updateTitleNumbers() {
        this.labelOrdersNumber.text = this.tableMultiPreassignments.selectedRows.length.toString();
        this.labelErrorsNumber.text = this.tableMultiPreassignments.rows.filter(row => row.enabled == false).length.toString();
    }

    labelOrderNumberOnClick(event: ClickEvent) {
        PlanningUtil.navigateToOrder(event);
    }

    labelManifestIdOnClick(event: ClickEvent) {
        const manifestId = (event.target as Label).getDataValue();
        PlanningUtil.navigateToManifest(manifestId);
    }

    async validateEquipment() {
        this.validationResults = await EquipmentGroupValidation.validateBatch(
            this.table.rows.map(row => ({ movement_id: row.data.get("movement_id") })),
            {
                tractorId: this.tractorId,
                driverId: this.driverId,
                driver2Id: this.driver2Id,
                preassignment: true,
            }
        );
    }

    async validateAssignments() {
        if (this.lastValidCheckState == null || this.tableMultiPreassignments.rows.some(row => row.enabled)) {
            this.setLastValidCheckState();
        }
        await this.validateEquipment();
        this.table.rows.forEach(row => this.updateValidationState(row));
        this.applyErrorState();
        if (this.tractorId || this.driverId) {
            this.applyLastValidCheckState();
        }
        this.updateTitleNumbers();
    }

    updateValidationState(tableRow: TableRow) {
        if (this.validationResults == null)
            return;
        const validationResult = this.validationResults.get(tableRow.data.get("movement_id"))
        const checkbox = this.tableMultiPreassignments.selectCheckboxHandler.getCheckboxInRow(tableRow);
        const imageError = tableRow.findComponentById("imageError") as Image;
        imageError.visible = EquipmentGroupValidation.hasErrors(validationResult);
        checkbox.enabled = tableRow.enabled = !imageError.visible;
        if (imageError.visible) {
            this.addValidationResultTooltip(imageError, validationResult);
        }
        const imageWarning = tableRow.findComponentById("imageWarning") as Image;
        imageWarning.visible = !imageError.visible && EquipmentGroupValidation.hasWarnings(validationResult);
        if (imageWarning.visible) {
            this.addValidationResultTooltip(imageWarning, validationResult);
        }
        if (this.linkedId != null && this.hasError()) {
            this.applyErrorIfLinked();
        }
    }

    applyErrorIfLinked() {
        // For linked groups, if any row has an error, all rows should be disabled
        this.tableMultiPreassignments.rows.forEach(row => {
            const checkbox = this.tableMultiPreassignments.selectCheckboxHandler.getCheckboxInRow(row);
            checkbox.enabled = row.enabled = false;
        });
    }

    setLastValidCheckState() {
        const initializing = this.lastValidCheckState == null;
        if (initializing) {
            this.lastValidCheckState = new Map<number, boolean>();
        }
        this.tableMultiPreassignments.rows.forEach(row => {
            if (row.enabled || initializing)
                this.lastValidCheckState.set(row.index, initializing ? true : row.selected);
        });
    }

    applyLastValidCheckState() {
        this.tableMultiPreassignments.rows.forEach(row => {
            const wasSelected = this.lastValidCheckState.get(row.index);
            if (wasSelected && !row.selected && row.enabled) {
                this.tableMultiPreassignments.selectRow(row, SelectionType.DISTINCT, null);
            }
        });
    }

    applyErrorState() {
        this.tableMultiPreassignments.selectedRows = this.tableMultiPreassignments.selectedRows.filter(row => row.enabled);
    }

    addValidationResultTooltip(image: Image, validationResult: EquipmentGroupValidationResult) {
        const equipmentGroupWarnings = Layout.getLayout("lme/dispatch/asset/EquipmentGroupWarnings") as EquipmentGroupWarnings;
        const tooltipPanel = new Panel({ components: [equipmentGroupWarnings] });
        equipmentGroupWarnings.addLayoutLoadListener(() => {
            equipmentGroupWarnings.display(validationResult);
            image.tooltipDynamicContent = tooltipPanel;
        });
    }

    handleServerErrors(errors: any[]) {
        this.tableMultiPreassignments.rows.filter(row => {
            return errors.some(mr => mr.error_key == row.data.get("movement_id"));
        }).forEach((row) => {
            const error = errors.find(mr => mr.error_key == row.data.get("movement_id"));
            this.updateRowWithServerError(row, error.message);
        });
        this.updateTitleNumbers();
    }

    updateRowWithServerError(tableRow: TableRow, error_message: string) {
        const checkbox = this.tableMultiPreassignments.selectCheckboxHandler.getCheckboxInRow(tableRow);
        const imageServerError = tableRow.findComponentById("imageServerError") as Image;
        imageServerError.setProps({
            visible: true,
            tooltip: error_message
        });
        this.tableMultiPreassignments.selectedRows = this.tableMultiPreassignments.selectedRows.filter(row => row != tableRow);
        tableRow.enabled = checkbox.enabled = tableRow.selected = false;
    }

    hasServerError(tableRow: TableRow): boolean {
        return (tableRow.findComponentById("imageServerError") as Image)?.visible ?? false;
    }

}
